import { ACTION, ROLE, type User } from '@talos/kyoko';
import { intersection, isEmpty } from 'lodash-es';
import { useCallback } from 'react';
import { OrgConfigurationKey, useOrgConfiguration } from '../providers/OrgConfigurationProvider';
import { useIsMobileLayout } from './useIsMobileLayout';
import { useUser } from './useUser';

// Exclude `talos.admin` here since it's a special role that bypasses all permission checks
export const RULES: Record<ACTION, Exclude<ROLE, (typeof ROLE)['TALOS_ADMIN']>[]> = {
  [ACTION.VIEW_ORDER]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.TRADER, ROLE.ADMIN],
  [ACTION.SUBMIT_ORDER]: [ROLE.TRADER],
  [ACTION.SUBMIT_ORDER_MOBILE]: [ROLE.MOBILE_UI_TRADER],
  [ACTION.PAUSE_ORDER]: [ROLE.TRADER, ROLE.ADMIN],
  [ACTION.RESUME_ORDER]: [ROLE.TRADER, ROLE.ADMIN, ROLE.TALOS_SUPPORT], // talos.support can resume orders, but not pause
  [ACTION.CANCEL_ORDER]: [ROLE.TRADER, ROLE.TALOS_CANCEL_ORDERS],
  [ACTION.ARCHIVE_ORDER]: [ROLE.TRADER, ROLE.ADMIN, ROLE.TALOS_SUPPORT], // TODO confirm with Kate
  [ACTION.FORCE_CANCEL_ORDER]: [ROLE.TALOS_SUPPORT],
  [ACTION.FORCE_FILL_ORDER]: [ROLE.TALOS_SUPPORT, ROLE.ADMIN],
  [ACTION.REQUEST_RFQ]: [ROLE.TRADER],
  [ACTION.REQUEST_RFQ_MOBILE]: [ROLE.MOBILE_UI_TRADER],
  [ACTION.ACCEPT_RFQ]: [ROLE.TRADER],
  [ACTION.ACCEPT_RFQ_MOBILE]: [ROLE.MOBILE_UI_TRADER],
  [ACTION.VIEW_ORDER_FORM]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER, ROLE.TRADER, ROLE.ADMIN],
  [ACTION.VIEW_RFQ_FORM]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER, ROLE.TRADER, ROLE.ADMIN],
  [ACTION.VIEW_SALES_ORDER_FORM]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER, ROLE.TRADER, ROLE.ADMIN],
  [ACTION.VIEW_SALES_RFQ_FORM]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER, ROLE.TRADER, ROLE.ADMIN],
  [ACTION.EDIT_MARKET_SETTINGS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.TRADING_CONFIG],
  [ACTION.EDIT_AGGREGATION_SETTINGS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.TRADING_CONFIG],
  [ACTION.EDIT_SUB_ACCOUNTS]: [ROLE.ADMIN, ROLE.TRADING_CONFIG, ROLE.TALOS_SUPPORT],
  [ACTION.CLONE_SUB_ACCOUNTS]: [ROLE.ADMIN, ROLE.TRADING_CONFIG, ROLE.TRADER, ROLE.TALOS_SUPPORT],
  [ACTION.SUBMIT_TRANSFER]: [ROLE.TRANSFERS],
  [ACTION.EDIT_LIMITS]: [ROLE.ADMIN, ROLE.TRADING_CONFIG, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_MAX_ORDER_SIZE_LIMITS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_COUNTERPARTY_LIMITS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_BALANCE]: [ROLE.TRADER, ROLE.ADMIN],
  [ACTION.VIEW_DEALER]: [ROLE.TALOS_VIEWER, ROLE.DEALER_VIEWER, ROLE.DEALER, ROLE.TALOS_SUPPORT, ROLE.DEALER_ADMIN_OPS],
  [ACTION.DEALER_TRADING]: [ROLE.DEALER, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_DEALER]: [ROLE.DEALER, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_EXECUTION_RULES_MODE]: [ROLE.ADMIN, ROLE.DEALER, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_DEALER_TRADING]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.VIEW_ORDER_ANALYTICS]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.DEALER],
  [ACTION.FORCE_WITHDRAWAL]: [ROLE.DEALER_ADMIN_OPS, ROLE.TALOS_SUPPORT],
  [ACTION.VIEW_PORTFOLIO]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.PORTFOLIO],
  [ACTION.EDIT_RESEARCH]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.RESEARCH_EDITOR, ROLE.PORTFOLIO_ENGINEERING],
  [ACTION.EDIT_REBALANCE]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.REBALANCE_EDITOR, ROLE.PORTFOLIO_ENGINEERING],
  [ACTION.EDIT_OPERATIONS]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.OPERATIONS_EDITOR, ROLE.PORTFOLIO_ENGINEERING],
  [ACTION.EDIT_SECMASTER]: [ROLE.TRADER, ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.SECMASTER],
  [ACTION.EDIT_SECMASTER_ADVANCED]: [ROLE.TALOS_SUPPORT, ROLE.SECMASTER, ROLE.ADMIN],
  [ACTION.APPROVE_PENDING_TRANSACTION]: [ROLE.TREASURY],
  [ACTION.APPROVE_PENDING_TRANSFER]: [ROLE.TREASURY],
  [ACTION.VIEW_TALOS_ADMIN_SETTINGS]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_FILTER_PERMISSIONS]: [ROLE.TALOS_SUPPORT, ROLE.ADMIN, ROLE.TRADING_CONFIG, ROLE.USER_ADMIN],
  [ACTION.EDIT_MARKET_ACCOUNT_FILTER_PERMISSIONS]: [ROLE.ADMIN, ROLE.TRADING_CONFIG, ROLE.TALOS_SUPPORT],
  [ACTION.VIEW_USER_GROUPS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],
  [ACTION.VIEW_FILTER_PERMISSIONS_FEATURE_DISABLED]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],
  [ACTION.EDIT_CONFIGURATIONS]: [ROLE.TALOS_SUPPORT],
  [ACTION.BYPASS_FILTER_PERMISSIONS_RESTRICTIONS_SUBACCOUNTS]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.ADMIN],
  [ACTION.EDIT_ROLLUPS]: [ROLE.ADMIN, ROLE.TRADING_CONFIG, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_MARKET_SESSION_LIMIT]: [ROLE.TALOS_SUPPORT, ROLE.ADMIN, ROLE.USER_ADMIN],
  [ACTION.VIEW_ANALYTICS]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.ANALYTICS],
  [ACTION.VIEW_INTEL]: [ROLE.TALOS_VIEWER, ROLE.TALOS_SUPPORT, ROLE.INTEL],
  [ACTION.VIEW_ADDRESSES]: [ROLE.TALOS_SUPPORT, ROLE.DEALER, ROLE.PORTFOLIO],
  [ACTION.TOGGLE_KILLSWITCH]: [ROLE.ADMIN],
  [ACTION.MANUAL_ORDER_FILL]: [ROLE.TALOS_SUPPORT, ROLE.ADMIN],
  [ACTION.MANUAL_RFQ_PRICING]: [ROLE.TALOS_SUPPORT, ROLE.TRADER, ROLE.ADMIN],
  [ACTION.EDIT_USERS]: [ROLE.USER_ADMIN, ROLE.TALOS_SUPPORT],
  // Talos Viewer may see the blotter but not administer it; talos.admin bypasses these rules.
  [ACTION.VIEW_SERVICE_ACCOUNTS]: [ROLE.SERVICE_ACCOUNTS_ADMIN, ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],
  [ACTION.EDIT_SERVICE_ACCOUNTS]: [ROLE.SERVICE_ACCOUNTS_ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.VIEW_USERS_HISTORY]: [ROLE.USER_ADMIN, ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],
  [ACTION.EDIT_USERS_DEFAULT_SPACE]: [ROLE.ADMIN, ROLE.USER_ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_NEW_USERS_DEFAULT_SPACE]: [ROLE.SPACES_ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.LIST_ALL_SPACES]: [ROLE.SPACES_ADMIN, ROLE.ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_ALL_SPACES]: [ROLE.SPACES_ADMIN, ROLE.ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_MARKET_CREDENTIALS]: [ROLE.USER_ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_CUSTOMER_BALANCE]: [ROLE.DEALER, ROLE.TALOS_SUPPORT, ROLE.DEALER_ADMIN_OPS],
  [ACTION.EDIT_CUSTOMER_USERS]: [ROLE.DEALER, ROLE.TALOS_SUPPORT, ROLE.DEALER_ADMIN_OPS],
  [ACTION.USE_ORGANIZATION]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],
  [ACTION.MORPH_USER]: [ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_POSITION]: [ROLE.TALOS_SUPPORT, ROLE.ADMIN, ROLE.TREASURY],
  [ACTION.EDIT_TRADE]: [ROLE.TALOS_SUPPORT, ROLE.DEALER_ADMIN_OPS],
  [ACTION.EDIT_ORGANIZATIONS]: [ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_MARKET_ACCOUNTS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.VIEW_PMS_LAYOUT_SETTING]: [ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_MARKET_CONNECTIONS]: [ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_PROCESSES]: [ROLE.TALOS_SUPPORT],
  [ACTION.VIEW_CREDENTIALS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.CREDENTIALS_ADMIN, ROLE.TALOS_VIEWER],
  [ACTION.EDIT_CREDENTIALS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.CREDENTIALS_ADMIN],
  [ACTION.VIEW_UNIFIED_LIQUIDITY_TOKENS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.TRADER],
  [ACTION.EDIT_UNIFIED_LIQUIDITY_TOKENS]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.TRADING_CONFIG],
  [ACTION.EDIT_CUSTOMER_FIX_CONNECTIONS]: [ROLE.TALOS_SUPPORT],
  [ACTION.VIEW_TRADING_CONTROLS]: [
    ROLE.ADMIN,
    ROLE.TALOS_SUPPORT,
    ROLE.TALOS_VIEWER,
    ROLE.TRADING_CONFIG,
    ROLE.TRADING_CONTROLS_ADMIN,
  ],
  [ACTION.REQUEST_EQUITY_MARGIN_RATE_LIMIT]: [ROLE.ADMIN, ROLE.TRADING_CONFIG, ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],
  [ACTION.VIEW_TRADING_CONTROLS_DIALOG]: [ROLE.ADMIN, ROLE.TRADING_CONFIG], // no talos role is included, but TalosAdmin bypasses all so its implicitly included
  [ACTION.VIEW_ALL_MARKET_ACCOUNTS_IN_TRADING_CONTROLS]: [
    ROLE.ADMIN,
    ROLE.TALOS_SUPPORT,
    ROLE.TALOS_VIEWER,
    ROLE.TALOS_ONBOARDING_ADMIN,
  ],
  // TODO: Remove this once customer dropdowns don't depend on the normal MarketAccounts subscription
  [ACTION.VIEW_ALL_MARKET_ACCOUNTS_IN_ACCOUNT_SEG]: [ROLE.DEALER, ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],
  [ACTION.VIEW_CONNECTION_STATUS]: [
    ROLE.ADMIN,
    ROLE.CREDENTIALS_ADMIN,
    ROLE.TALOS_SUPPORT,
    ROLE.TALOS_VIEWER,
    ROLE.TALOS_ONBOARDING_ADMIN,
    ROLE.TALOS_CANCEL_ORDERS,
  ],
  [ACTION.VIEW_SECURITY_MASTER]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER, ROLE.TALOS_ONBOARDING_ADMIN],
  [ACTION.EDIT_PORTFOLIO_ENGINEERING]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER, ROLE.PORTFOLIO_ENGINEERING],
  [ACTION.VIEW_CUSTOMER_CONFIGURATIONS]: [ROLE.TALOS_VIEWER, ROLE.DEALER_VIEWER, ROLE.DEALER, ROLE.TALOS_SUPPORT], // + ui.ShowCustomerTiering config must also be true.
  [ACTION.EDIT_CUSTOMER_CONFIGURATIONS]: [
    ROLE.DEALER,
    ROLE.DEALER_CONFIGURATION,
    ROLE.TALOS_SUPPORT,
    ROLE.DEALER_ADMIN_OPS,
  ],
  [ACTION.VIEW_CUSTOMER_PRICING_TIERS]: [ROLE.TALOS_VIEWER, ROLE.DEALER_VIEWER, ROLE.DEALER, ROLE.TALOS_SUPPORT], // + ui.ShowCustomerTiering config must also be true.
  [ACTION.VIEW_CUSTOMER_SYMBOL_GROUPS]: [ROLE.TALOS_VIEWER, ROLE.DEALER_VIEWER, ROLE.DEALER, ROLE.TALOS_SUPPORT], // + ui.ShowCustomerTiering config must also be true.
  [ACTION.VIEW_CUSTOMER_ACQUISITION_LIMITS]: [ROLE.TALOS_VIEWER, ROLE.DEALER_VIEWER, ROLE.DEALER, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_CUSTOMER_ACQUISITION_LIMITS]: [ROLE.DEALER, ROLE.TALOS_SUPPORT, ROLE.DEALER_ADMIN_OPS],
  [ACTION.SUBMIT_CARE_ORDER]: [ROLE.TRADER, ROLE.ADMIN],
  [ACTION.APPROVE_CARE_ORDER_COMPLIANCE]: [ROLE.PORTFOLIO_COMPLIANCE_ADMIN],
  [ACTION.FORCE_CANCEL_CARE_ORDER]: [ROLE.TRADER_ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.FORCE_CANCEL_SPREADTOFIXING_ORDER]: [ROLE.TRADER_ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_ORDER_FIXING_DETAILS]: [ROLE.TRADER_ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.VIEW_MONITORING_ORDER_CONTEXT]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],

  [ACTION.EDIT_PORTFOLIO_RECON]: [ROLE.TREASURY, ROLE.TALOS_SUPPORT],
  [ACTION.ADD_ABOR_RECON_COMMENT]: [ROLE.ADMIN, ROLE.TREASURY, ROLE.TALOS_SUPPORT],
  [ACTION.HEDGER_SYNCHRONIZATION]: [ROLE.TALOS_SUPPORT, ROLE.AUTOHEDGING_ADMIN],
  [ACTION.VIEW_LEDGER_EVENTS]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER],
  [ACTION.VIEW_AUTOHEDGING]: [ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER, ROLE.AUTOHEDGING_VIEW, ROLE.AUTOHEDGING_ADMIN],
  [ACTION.EDIT_AUTOHEDGING]: [ROLE.TALOS_SUPPORT, ROLE.AUTOHEDGING_ADMIN],
  [ACTION.EDIT_BASKET]: [ROLE.TALOS_SUPPORT, ROLE.BASKET_EDITOR, ROLE.ADMIN],

  [ACTION.EDIT_NOTIFICATIONS_ADMIN]: [ROLE.ADMIN, ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_NOTIFICATIONS_DESTINATIONS_SERVICE_CONFIG]: [ROLE.TALOS_SUPPORT],

  // Counterparties support (LP and OTC)
  [ACTION.EDIT_COUNTERPARTIES]: [ROLE.TALOS_SUPPORT, ROLE.TRADING_CONFIG, ROLE.ADMIN],
  [ACTION.MANAGE_COUNTERPARTY_EXTERNAL_ID_TYPES]: [ROLE.TALOS_SUPPORT, ROLE.ADMIN],

  [ACTION.VIEW_LP_SETTLEMENT]: [ROLE.TALOS_SUPPORT, ROLE.LP_SETTLEMENT],
  [ACTION.EDIT_LP_SETTLEMENT]: [ROLE.LP_SETTLEMENT],

  [ACTION.VIEW_CREDIT_AND_BALANCES]: [
    ROLE.PORTFOLIO,
    ROLE.ADMIN,
    ROLE.TALOS_SUPPORT,
    ROLE.TALOS_VIEWER,
    ROLE.TALOS_ONBOARDING_ADMIN,
    ROLE.TALOS_CANCEL_ORDERS,
  ],

  // OTC functionality
  [ACTION.CREATE_CUSTOM_OTC_INSTRUMENT]: [ROLE.TRADER],
  [ACTION.SUBMIT_DEAL]: [ROLE.TRADER],
  [ACTION.CANCEL_DEAL]: [ROLE.TRADER],
  [ACTION.VIEW_PRODUCT_TEMPLATES_ADMIN]: [ROLE.TALOS_SUPPORT],
  [ACTION.EDIT_PRODUCT_TEMPLATES_ADMIN]: [ROLE.TALOS_SUPPORT],

  // Trading Aggregations view permission
  [ACTION.VIEW_TRADING_AGGREGATIONS]: [ROLE.ADMIN, ROLE.TRADING_CONFIG],
};

export const useRoleAuth = () => {
  const user = useUser();
  // Shared layout context (not useIsMobile) so resize does not re-render every useRoleAuth caller.
  const isMobileLayout = useIsMobileLayout();
  const { getConfig } = useOrgConfiguration();
  const requireMobileUITraderRole = getConfig(OrgConfigurationKey.RequireMobileUITraderRole, false);
  const isAuthorized = useCallback(
    (action: ACTION) => isUserAuthorized(user, action, isMobileLayout, RULES, requireMobileUITraderRole),
    [isMobileLayout, requireMobileUITraderRole, user]
  );
  const isRole = useCallback((role: ROLE) => isUserRole(user, role), [user]);

  return { isAuthorized, isRole };
};

// Order/RFQ trading permissions can be split by device; other actions use the same permission on desktop and mobile.
const MOBILE_ACTIONS: Partial<Record<ACTION, ACTION>> = {
  [ACTION.SUBMIT_ORDER]: ACTION.SUBMIT_ORDER_MOBILE,
  [ACTION.REQUEST_RFQ]: ACTION.REQUEST_RFQ_MOBILE,
  [ACTION.ACCEPT_RFQ]: ACTION.ACCEPT_RFQ_MOBILE,
};

export function getDeviceSpecificAction(action: ACTION, isMobile: boolean, requireMobileUITraderRole = false): ACTION {
  if (!isMobile || !requireMobileUITraderRole) {
    return action;
  }

  return MOBILE_ACTIONS[action] ?? action;
}

export const isUserRole = (user: User, role: ROLE): boolean => Boolean(user?.Roles?.includes(role));

export const isUserMonitor = (user: User) => user?.Name?.startsWith('Monitor-');

export const isUserAuthorized = (
  user: User,
  action: ACTION,
  isMobile: boolean | undefined,
  rules: typeof RULES = RULES,
  requireMobileUITraderRole = false
) => {
  if (user == null || user.Roles == null || isEmpty(user.Roles)) {
    return false;
  }

  // Bypass permission checks for talos.admin
  if (user.Roles.includes(ROLE.TALOS_ADMIN)) {
    return true;
  }

  return !isEmpty(
    intersection(user.Roles, rules[getDeviceSpecificAction(action, isMobile ?? false, requireMobileUITraderRole)])
  );
};
